/**
 * Готовый текст КП для заказчика — МСК Газобетон.
 *
 * Своих расчётов здесь нет. Позиции, количества, цены и суммы берутся из уже
 * собранных строк блоков КП (buildGazCommercialProposalBlocks), итог к оплате —
 * из существующего gazCashRounding. Ни материалов, ни доставки, ни транспорта
 * этот модуль не считает и не выбирает.
 *
 * Текст клиентский: служебных строк («По заказу №…», подпись менеджера, внутренняя
 * разница округления) в нём нет. Разница округления остаётся видна менеджеру в
 * верхней сводке КП — в сообщение заказчику она не попадает.
 *
 * Кроме текста модуль отдаёт ту же разбивку строками (buildGazClientProposalModel):
 * по ней менеджер правит цены руками. Ручная цена накладывается на уже посчитанную
 * строку, суммы и итог пересобираются теми же функциями — второго расчёта КП
 * ни для экрана, ни для правки цен не заводится.
 */

import buildGazCommercialProposalBlocks from './buildGazCommercialProposalBlocks.js';
import { resolveGazCashTotal } from './gazCashRounding.js';
import { buildGazSlingerNoticeText } from './gazSlingerNotice.js';
import { buildVtoroySortMessageLines } from './gazVtoroySort.js';
import { isGazManipulatorUnloadMethod } from '../Information/gazUnloadMethod.js';
import { tripsWord } from '../../utils/gazDeclensions.js';
import { formatMoney, formatNomenclatureName, formatNumber, formatUnit } from '../../utils/gazTextFormat.js';
import {
  applyGazManualPrice,
  gazPriceLinesKey,
  normalizeGazPriceOverrides,
  GAZ_PRICE_GROUP_DELIVERY,
  GAZ_PRICE_GROUP_MATERIAL,
  GAZ_PRICE_GROUP_UNLOADING,
  GAZ_UNLOADING_UNIT,
} from './gazProposalManualPrices.js';

// Понятное имя типа доставки. Только подмена подписи: строка, её количество,
// цена и сумма берутся из расчёта как есть.
const DELIVERY_LABELS = {
  'main-shalanda': 'Шаланда',
  'secondary-shalanda': 'Шаланда',
};

/** Рейсы манипулятора: и тот, что заодно разгружает, и тот, что только везёт. */
const MANIPULATOR_TRIP_KEYS = new Set([
  'secondary-manipulator-trip',
  'secondary-manipulator-trip-no-unload',
]);

/** Рейс, которым в расчёте едет оплаченная манипуляторная разгрузка. */
const MANIPULATOR_UNLOADING_TRIP_KEY = 'secondary-manipulator-trip';

const MANIPULATOR_LABEL = 'Манипулятор';
// «Всего объёма» — не украшение, а снятие путаницы: при выбранной разгрузке
// манипулятором он снимает ВЕСЬ заказ, включая то, что привезла шаланда, а не
// только собственный груз. Прежняя подпись «Манипулятор с разгрузкой» читалась
// менеджерами и заказчиками как «разгрузит себя» (Роман, 19.10.2026).
const MANIPULATOR_UNLOADING_LABEL = 'Манипулятор с разгрузкой всего объёма';

/**
 * Как манипулятор называется в КП.
 *
 * «С разгрузкой» — только когда заказчик выбрал разгрузку манипулятором и это
 * тот самый рейс, в котором она посчитана. При «без разгрузки» и при разгрузке
 * краном манипулятор товар только везёт: обещать заказчику, что он снимет
 * шаланду или собственный груз, КП не должно. Признак — выбранный способ
 * разгрузки из расчёта, а не стоимость рейса, наличие шаланды и не название
 * машины.
 */
const resolveDeliveryLabel = (row, unloadMethod) => {
  if (MANIPULATOR_TRIP_KEYS.has(row?.key)) {
    return isGazManipulatorUnloadMethod(unloadMethod) && row.key === MANIPULATOR_UNLOADING_TRIP_KEY
      ? MANIPULATOR_UNLOADING_LABEL
      : MANIPULATOR_LABEL;
  }
  return DELIVERY_LABELS[row?.key] || row?.name;
};

/** Пробелы неразрывные ломают вставку в мессенджер — разделяем разряды обычным. */
const money = (value) => formatMoney(value);

// Значения те же: общий форматтер даёт ту же запись, а нечисло здесь по-прежнему
// показывается нулём.
const quantityText = (value) => formatNumber(value) ?? '0';

// «м3» в номенклатуре и «м³» в документах — одна и та же единица, отличается глиф.
// «рейс» склоняется по числу: КП уходит заказчику, «2 рейс» там читается ошибкой.
// Склонение берётся из общей утилиты — второй системы склонений не заводится.
const unitText = (unit, quantity) => {
  const value = formatUnit(unit);
  if (value === 'м³') {
    return value;
  }
  if (value === 'рейс') {
    return tripsWord(quantity);
  }
  return value;
};

const isDeliveryRow = (row) => row?.kind !== 'material';

/**
 * Одинаковая номенклатура из разных блоков (полные машины + остаток) — одна позиция
 * заказчика. Складываются только уже посчитанные количества и суммы.
 */
const mergeMaterialRows = (rows) => {
  const grouped = new Map();

  for (const row of rows) {
    const key = `${row.name}__${row.unit}__${Number(row.pricePerOne) || 0}`;
    const existing = grouped.get(key);
    if (existing) {
      existing.quantity += Number(row.quantity) || 0;
      existing.sum += Number(row.sum) || 0;
      continue;
    }
    grouped.set(key, {
      name: row.name,
      unit: row.unit,
      pricePerOne: Number(row.pricePerOne) || 0,
      quantity: Number(row.quantity) || 0,
      sum: Number(row.sum) || 0,
    });
  }

  return Array.from(grouped.values());
};

/** Рейсы одного типа транспорта — одна строка. */
const mergeDeliveryRows = (rows, unloadMethod) => {
  const grouped = new Map();

  for (const row of rows) {
    const label = resolveDeliveryLabel(row, unloadMethod);
    const key = `${label}__${row.unit}__${Number(row.pricePerOne) || 0}`;
    const existing = grouped.get(key);
    if (existing) {
      existing.quantity += Number(row.quantity) || 0;
      existing.sum += Number(row.sum) || 0;
      continue;
    }
    grouped.set(key, {
      name: label,
      unit: row.unit,
      pricePerOne: Number(row.pricePerOne) || 0,
      quantity: Number(row.quantity) || 0,
      sum: Number(row.sum) || 0,
    });
  }

  return Array.from(grouped.values());
};


const sumOf = (rows) => rows.reduce((sum, row) => sum + (Number(row.sum) || 0), 0);

/** Разгрузка приходит из расчёта отдельной услугой, рейс — рейсом. */
const groupOfDeliveryRow = (row) => (
  formatUnit(row?.unit) === GAZ_UNLOADING_UNIT ? GAZ_PRICE_GROUP_UNLOADING : GAZ_PRICE_GROUP_DELIVERY
);

/**
 * Разбор КП на строки, суммы и итог — с наложенными ручными ценами.
 *
 * Своих формул здесь по-прежнему нет: строки, количества и цены расчёта берутся
 * у buildGazCommercialProposalBlocks, ручная цена накладывается поверх строки
 * (gazProposalManualPrices), итог к оплате округляет gazCashRounding. Без ручных
 * цен модель повторяет прежний расчёт до копейки, а текст — прежний текст.
 *
 * @param {object}  params
 * @param {object}  params.offer                — предложение (уже подготовленное под вариант КП)
 * @param {string}  params.paymentMethod        — форма оплаты
 * @param {boolean} params.isMskGazobetonScope  — признак области, приходит готовым
 * @param {object}  [params.priceOverrides]     — ручные цены по ключам строк
 */
/**
 * Ручная цена строки КП, если менеджер её задал.
 *
 * Ключ считается ровно теми же правилами, что и в модели КП: раздел, подпись строки,
 * единица и цена расчёта. Поэтому одна и та же строка даёт один и тот же ключ и на
 * экране, и в сборке товаров сделки — иначе клиенту называлась бы одна цена, а в
 * Битрикс уходила другая.
 *
 * Строка блока приходит НЕ склеенной, и это не мешает: склейка меняет только
 * количество и сумму, а имя, единица и цена у слагаемых одинаковые — иначе они бы
 * в одну строку не попали.
 *
 * @param {object} row          — строка блока КП
 * @param {object} params
 * @param {string} [params.unloadMethod] — способ разгрузки, для подписи рейса
 * @param {object} [params.overrides]    — ручные цены по ключам строк
 * @returns {number|null} цена, если она задана руками и отличается от расчётной
 */
export const resolveGazRowManualPrice = (row, { unloadMethod, overrides } = {}) => {
  const normalized = normalizeGazPriceOverrides(overrides);
  if (!row || Object.keys(normalized).length === 0) {
    return null;
  }

  const isMaterial = !isDeliveryRow(row);
  const group = isMaterial ? GAZ_PRICE_GROUP_MATERIAL : groupOfDeliveryRow(row);
  const name = isMaterial ? row.name : resolveDeliveryLabel(row, unloadMethod);
  const priced = applyGazManualPrice(
    { name, unit: row.unit, quantity: row.quantity, pricePerOne: row.pricePerOne, sum: row.sum },
    group,
    normalized
  );

  return priced.isManual ? priced.pricePerOne : null;
};

export const buildGazClientProposalModel = ({
  offer,
  paymentMethod,
  isMskGazobetonScope,
  priceOverrides,
} = {}) => {
  const empty = {
    isEmpty: true,
    materialLines: [],
    deliveryLines: [],
    unloadingLines: [],
    deliverySectionLines: [],
    materialsTotal: 0,
    deliveryTotal: 0,
    grandTotal: 0,
    payable: null,
    cashApplies: false,
    hasManualPrices: false,
    baseKey: '',
    text: '',
  };

  if (!isMskGazobetonScope || !offer) {
    return empty;
  }

  const proposal = buildGazCommercialProposalBlocks(offer, { isMskGazobetonScope: true });
  if (!proposal.isGazobetonOffer) {
    return empty;
  }

  const allRows = (proposal.blocks || []).flatMap((block) => (Array.isArray(block?.rows) ? block.rows : []));
  // Способ разгрузки — тот же, что стоит в переключателе: расчёт возвращает его
  // в разбивке доставки. Пересобирается текст вместе с расчётом, поэтому после
  // переключения в нём не остаётся подписи прежнего варианта.
  const unloadMethod = offer?.delivery?.deliveryBreakdown?.unloadMethod;
  const overrides = normalizeGazPriceOverrides(priceOverrides);

  const materialLines = mergeMaterialRows(allRows.filter((row) => row?.kind === 'material'))
    .map((row) => applyGazManualPrice(row, GAZ_PRICE_GROUP_MATERIAL, overrides));
  // Порядок раздела «Доставка» сохраняется как в расчёте: рейсы, потом услуги
  // разгрузки. Разделение на группы нужно только редактору цен.
  const deliverySectionLines = mergeDeliveryRows(allRows.filter(isDeliveryRow), unloadMethod)
    .map((row) => applyGazManualPrice(row, groupOfDeliveryRow(row), overrides));

  if (materialLines.length === 0 && deliverySectionLines.length === 0) {
    return empty;
  }

  const materialsTotal = sumOf(materialLines);
  const deliveryTotal = sumOf(deliverySectionLines);
  const grandTotal = materialsTotal + deliveryTotal;

  // Итог к оплате — тем же gazCashRounding, что и на экране. Наличные округляются,
  // безналичные остаются как есть. Разница округления в клиентский текст не идёт.
  const cash = resolveGazCashTotal({ total: grandTotal, isMskGazobetonScope: true, paymentMethod });
  const payable = cash.applies ? cash.total : grandTotal;

  // Вёрстка плотная: пустая строка разделяет только разделы, внутри раздела строки
  // идут подряд. В мессенджере лишние переносы растягивают КП на несколько экранов.
  const parts = ['Коммерческое предложение'];

  // Раздела нет — строки нет: пустых заголовков в КП не появляется.
  if (materialLines.length > 0) {
    const lines = materialLines.map((row) => (
      `${formatNomenclatureName(row.name)}\n${quantityText(row.quantity)} ${unitText(row.unit, row.quantity)} × ${money(row.pricePerOne)} ₽ = ${money(row.sum)} ₽`
    ));
    parts.push(`Материалы:\n${lines.join('\n')}\nИтого материалы: ${money(materialsTotal)} ₽`);
  }

  if (deliverySectionLines.length > 0) {
    const lines = deliverySectionLines.map((row) => (
      `${row.name} — ${quantityText(row.quantity)} ${unitText(row.unit, row.quantity)} × ${money(row.pricePerOne)} ₽ = ${money(row.sum)} ₽`
    ));
    parts.push(`Доставка:\n${lines.join('\n')}\nИтого доставка: ${money(deliveryTotal)} ₽`);
  }

  // Стропальщиков даёт заказчик — сразу после доставки с разгрузкой и до итога:
  // это условие разгрузки, а не отдельная строка расходов. Текст общий с карточкой
  // машины, показывать его или нет решает выбранный способ разгрузки из расчёта.
  const slingerNotice = buildGazSlingerNoticeText(offer?.delivery?.deliveryBreakdown);
  if (slingerNotice) {
    parts.push(slingerNotice);
  }

  // Условие подъезда заказчику не пишется: оно про пересогласование цены и
  // адресовано менеджеру — он видит его в интерфейсе, под машинами.

  // При наличных сумма уже округлена до 500 ₽ — об этом говорит сама подпись.
  // Размер округления клиенту не показывается.
  parts.push(`${cash.applies ? 'ИТОГО К ОПЛАТЕ НАЛИЧНЫМИ' : 'ИТОГО К ОПЛАТЕ'}: ${money(payable)} ₽`);

  // - Условия второго сорта — последним абзацем, после итога (13.10.2026, Роман:
  //   «почему в сообщении клиента не написана эта инфа?»). Этот текст менеджер видит
  //   на экране и копирует кнопкой, то есть именно он чаще всего и уходит заказчику;
  //   до правки условия дописывались только в сообщение, отправляемое в сделку.
  //   Пусто, если второго сорта в заказе нет.
  const vtoroySortLines = buildVtoroySortMessageLines(materialLines);
  if (vtoroySortLines.length > 0) {
    parts.push(vtoroySortLines.join('\n'));
  }

  const allLines = [...materialLines, ...deliverySectionLines];

  return {
    isEmpty: false,
    materialLines,
    deliveryLines: deliverySectionLines.filter((line) => line.group === GAZ_PRICE_GROUP_DELIVERY),
    unloadingLines: deliverySectionLines.filter((line) => line.group === GAZ_PRICE_GROUP_UNLOADING),
    deliverySectionLines,
    materialsTotal,
    deliveryTotal,
    grandTotal,
    payable,
    cashApplies: cash.applies,
    hasManualPrices: allLines.some((line) => line.isManual),
    baseKey: gazPriceLinesKey(allLines),
    text: parts.join('\n\n'),
  };
};

/**
 * @param {object}  params
 * @param {object}  params.offer                — предложение (уже подготовленное под вариант КП)
 * @param {string}  params.paymentMethod        — форма оплаты
 * @param {boolean} params.isMskGazobetonScope  — признак области, приходит готовым
 * @param {object}  [params.priceOverrides]     — ручные цены по ключам строк
 * @returns {string} готовый текст; пустая строка — показывать нечего
 */
export const buildGazClientProposalText = (params = {}) => buildGazClientProposalModel(params).text;

export default buildGazClientProposalText;
